import React, {Fragment} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';

import {Box, Typography} from '@mui/material';
import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {MainFooter, MainHeader, SubheaderWithSearch} from 'presentation/components';
import {Filter} from 'presentation/components/Filter';
import {
	ExamBookingEnum,
	GuidesEnum,
	HealthBookingEnum,
	OdontoBookingEnum,
	QueryParamsEnum,
} from 'presentation/constants';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {translations} from 'presentation/translations-new';
import {CHANGE_PLAN_LOCATION_FLOW, FlowEnumWithoutProfile, getInitialPath} from 'presentation/utils';
import {FlowEnum} from '../booking/constants';

import {UfList} from './components/UfList';
import {StyledBreadcrumbs} from './styles';
import {UfSelectionProps} from './types';
import {getUfSelectionHeaderTitle} from './utils/getUfSelectionHeaderTitle';

import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useGuideStore} from 'presentation/modules/guide/shared/store/guideStore';
import {GuideStep} from 'presentation/modules/guide/shared/constants/constants';

const {
	state: {title, searchStates, locationStep},
} = translations['pt-br'].privateFlow.profile.locationSelection;
const {state: stateTitle} = translations['pt-br'].commons;

export function UfSelection({flowType}: UfSelectionProps) {
	const navigate = useNavigate();
	const {state: useLocationState} = useLocation();
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const bookingData = useBookingStore(state => state.bookingData);
	const isExamFlow = flowType === FlowEnum.EXAM;
	const isHealthFlow = flowType === FlowEnum.HEALTH;
	const isGuideFlow = flowType === FlowEnum.HEALTH_GUIDE || flowType === FlowEnum.ODONTO_GUIDE;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const isChangePlanFlow = flowType === CHANGE_PLAN_LOCATION_FLOW;
	const flowTypeForBookingComponents: FlowEnumWithoutProfile = isChangePlanFlow
		? FlowEnum.HEALTH
		: flowType;
	const pageTitle = getUfSelectionHeaderTitle(flowTypeForBookingComponents);
	const hideSubheader = isExamFlow || isHealthFlow || isGuideFlow || isOdontoFlow;
	const initialPath = getInitialPath(flowType);
	const bookingFlowPath = () => {
		if (isExamFlow) return ExamBookingEnum.EXAM_BOOKING_FLOW;
		if (isOdontoFlow) return OdontoBookingEnum.ODONTO_BOOKING_FLOW;

		return HealthBookingEnum.HEALTH_BOOKING_FLOW;
	};
	const {LOCATION_STEP} = getStepPosition({
		flowType: flowTypeForBookingComponents,
		isManualLocation: bookingData?.isManualLocation,
	});
	const guideFlowPath =
		flowType === FlowEnum.ODONTO_GUIDE
			? GuidesEnum.SERVICE_NETWORK_ODONTO
			: GuidesEnum.SERVICE_NETWORK_HEALTH;

	const resetHealthStates = useGuideStore(state => state.resetHealthStates);
	const resetOdontoStates = useGuideStore(state => state.resetOdontoStates);
	const resetGuideStates = () => {
		if (flowType === FlowEnum.ODONTO_GUIDE) {
			resetOdontoStates();
			return;
		}
		resetHealthStates();
	};

	const handleNavigateBack = () => {
		if (isHealthFlow || isExamFlow || isOdontoFlow) {
			setActiveBookingFlowStep(LOCATION_STEP);
			navigate(bookingFlowPath());
			return;
		}

		if (isGuideFlow) {
			resetGuideStates();
			navigate(`${guideFlowPath}?${QueryParamsEnum.STEP}=${GuideStep.LOCATION}`);
			return;
		}

		if (flowType === FlowEnum.AUTHORIZATIONS) {
			navigate(initialPath, {state: {forceLocationStep: true}});
			return;
		}

		navigate(initialPath);
	};

	const hasNotSpecialtiesAtCoordinate = useLocationState?.hasNotSpecialtiesAtCoordinate;
	const showBreadcrumbs = isHealthFlow || isExamFlow || isGuideFlow || isOdontoFlow;

	return (
		<Fragment>
			<MainHeader
				title={pageTitle}
				handleBack={handleNavigateBack}
				handleClose={handleNavigateBack}
				variant="subRoute">
				{hideSubheader ? null : (
					<SubheaderWithSearch title={title} placeholder={searchStates} />
				)}
				{showBreadcrumbs ? (
					<Fragment>
						<StyledBreadcrumbs>
							{hasNotSpecialtiesAtCoordinate ? (
								<Link to={bookingFlowPath()}>
									<Typography
										variant="body1"
										fontWeight={600}
										color="white"
										sx={{textDecoration: 'underline'}}>
										{locationStep}
									</Typography>
								</Link>
							) : null}

							<Typography variant="body1" color="white">
								{stateTitle}
							</Typography>
						</StyledBreadcrumbs>
					</Fragment>
				) : null}

				<Filter.Root>
					<Filter.Search placeholder={searchStates} />
				</Filter.Root>
			</MainHeader>

			<UfList
				ufDataTagId={AppointmentBookingEnum.SELECIONOU_ESTADO}
				flowType={flowType}
				handleBack={handleNavigateBack}
			/>

			<Box mt="auto">
				<MainFooter />
			</Box>
		</Fragment>
	);
}
